export const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', error => reject(error));
        image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
        image.src = url;
    });

export function getRadianAngle(degreeValue: number) {
    return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width: number, height: number, rotation: number) {
    const rotRad = getRadianAngle(rotation);

    return {
        width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
        height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
    };
}

/**
 * Longest-edge cap for the exported crop. Cloudinary rejects transforms (e.g. the `e_trim` applied
 * to the sidebar team logo) for assets over 50 megapixels, so a print-res upload would fail to
 * render. 2048px keeps the output well under that limit while staying sharp for any on-screen use.
 */
export const MAX_CROP_OUTPUT_DIMENSION = 2048;

export function clampToMaxDimension(width: number, height: number, max: number): { width: number; height: number } {
    const longest = Math.max(width, height);
    if (longest <= max) return { width, height };
    const scale = max / longest;
    return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

function downscaleCanvas(source: HTMLCanvasElement, width: number, height: number): HTMLCanvasElement {
    const out = document.createElement('canvas');
    out.width = width;
    out.height = height;
    const outCtx = out.getContext('2d');
    if (!outCtx) return source; // fall back to full-res if a 2D context is unavailable
    outCtx.drawImage(source, 0, 0, source.width, source.height, 0, 0, width, height);
    return out;
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
export async function getCroppedImg(
    imageSrc: { url: string; title: string },
    pixelCrop: { x: number; y: number; width: number; height: number },
    rotation = 0,
    flip = { horizontal: false, vertical: false }
): Promise<string | null> {
    const image = await createImage(imageSrc.url);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
        return null;
    }

    const rotRad = getRadianAngle(rotation);

    // calculate bounding box of the rotated image
    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

    // set canvas size to match the bounding box
    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    // translate canvas context to a central location to allow rotating and flipping around the center
    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotRad);
    ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
    ctx.translate(-image.width / 2, -image.height / 2);

    // draw rotated image
    ctx.drawImage(image, 0, 0);

    // croppedAreaPixels values are bounding box relative
    // extract the cropped image using these values
    const data = ctx.getImageData(pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height);

    // set canvas width to final desired crop size - this will clear existing context
    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    // paste generated rotate image at the top left corner
    ctx.putImageData(data, 0, 0);

    // Cap the exported dimensions so a print-res source (e.g. a 15643x5493 logo) doesn't upload as
    // an 80+ megapixel asset that Cloudinary then can't transform. Small crops pass through untouched.
    const target = clampToMaxDimension(canvas.width, canvas.height, MAX_CROP_OUTPUT_DIMENSION);
    const output =
        target.width === canvas.width && target.height === canvas.height
            ? canvas
            : downscaleCanvas(canvas, target.width, target.height);

    // As a blob
    return new Promise(resolve => {
        output.toBlob(file => {
            if (file) resolve(URL.createObjectURL(file));
        }, 'image/png');
    });
}
